import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { getMerchants } from '@/features/merchants/api';
import { MerchantStatusSchema } from '@/features/merchants/merchant';
import type { MerchantStatus } from '@/features/merchants/merchant';
import { MerchantStatusBadge } from '@/features/merchants/MerchantStatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ShoppingBag,
  Plus,
  CheckCircle,
  Clock,
  XCircle,
  DollarSign,
  Building,
} from '@/lib/icons';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { ListFilterBar, NoFilterResults } from '@/components/filters';
import type { FilterChip } from '@/components/filters';
import { useListFilters } from '@/hooks/use-list-filters';
import { useAppSelector } from '@/store/hooks';
import { isSuperAdmin } from '@/lib/auth/roles';
import { formatDate } from '@/lib/dates';

const merchantsSearchSchema = z.object({
  q: z.string().optional(),
  status: MerchantStatusSchema.optional(),
});

export const Route = createFileRoute('/_layout/merchants/')({
  validateSearch: merchantsSearchSchema,
  component: MerchantsPage,
});

const buildStatusChips = (
  t: (key: string) => string
): ReadonlyArray<FilterChip<'all' | MerchantStatus>> => [
  { value: 'all', label: t('merchants.filters.all') },
  { value: 'active', label: t('merchants.status.active') },
  { value: 'pending_review', label: t('merchants.status.pendingReview') },
  { value: 'needs_change', label: t('merchants.status.needsChange') },
  { value: 'not_approved', label: t('merchants.status.notApproved') },
  { value: 'suspended', label: t('merchants.status.suspended') },
];

const modeBadge = (mode: string) => {
  const colors: Record<string, string> = {
    modeA: 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
    modeB:
      'bg-violet-500/15 text-violet-600 border-violet-500/30 dark:text-violet-400',
  };
  const labels: Record<string, string> = {
    modeA: 'Mode A',
    modeB: 'Mode B',
  };
  return (
    <Badge className={colors[mode] || ''}>
      <DollarSign className="w-3 h-3 mr-1" />
      {labels[mode] || mode}
    </Badge>
  );
};

const avatar = (name: string) => {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const colors = [
    'bg-teal-500',
    'bg-indigo-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-cyan-500',
    'bg-lime-500',
  ];
  const idx = name.charCodeAt(0) % colors.length;
  return (
    <div
      className={`w-9 h-9 rounded-full ${colors[idx]} flex items-center justify-center text-white text-xs font-bold shrink-0`}
    >
      {initials}
    </div>
  );
};

function MerchantsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();

  const {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearchPending,
    setFilters,
    reset,
    activeCount,
  } = useListFilters({ search, to: '/merchants' });
  const user = useAppSelector((state) => state.auth.user);
  const selectedTenantId = useAppSelector(
    (state) => state.tenant.selectedTenantId
  );

  // This screen is superuser-only (see lib/auth/superuser-scopes), so its
  // visitor is normally a cross-tenant operator with no tenant on the JWT.
  // Merchants are tenant-scoped, and the endpoint 400s without a tenant — so
  // hold the request until one is picked from the sidebar selector rather than
  // firing a doomed call and rendering an error the operator can't act on.
  const awaitingTenantChoice =
    isSuperAdmin(user) && !user?.tenantId && !selectedTenantId;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    // Keyed on the tenant so a switch shows that tenant's list rather than the
    // previous one while the refetch is in flight.
    queryKey: ['merchants', selectedTenantId, debouncedSearch, search.status],
    queryFn: () =>
      getMerchants({ search: debouncedSearch, status: search.status }),
    refetchOnMount: 'always',
    enabled: !awaitingTenantChoice,
    placeholderData: (previous) => previous,
  });

  const allMerchants = data?.value ?? [];

  const countByStatus = (...statuses: MerchantStatus[]) =>
    allMerchants.filter((m) => m.status && statuses.includes(m.status)).length;

  const stats = [
    {
      label: 'Total Merchants',
      value: allMerchants.length,
      icon: ShoppingBag,
      color: 'from-teal-500 to-teal-600',
    },
    {
      label: t('merchants.status.active'),
      value: countByStatus('active'),
      icon: CheckCircle,
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      label: t('merchants.stats.inReview'),
      value: countByStatus('pending_review', 'needs_change'),
      icon: Clock,
      color: 'from-amber-500 to-amber-600',
    },
    {
      label: t('merchants.status.suspended'),
      value: countByStatus('suspended'),
      icon: XCircle,
      color: 'from-red-400 to-red-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Merchants</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage merchant onboarding, credit accounts, and receivables
          </p>
        </div>
        <Button
          className="bg-gradient-to-r from-primary to-primary/85 hover:from-primary/90 hover:to-primary/75 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200"
          onClick={() => navigate({ to: '/merchants/new' })}
          disabled={awaitingTenantChoice}
        >
          <Plus className="w-4 h-4 mr-2" /> Add Merchant
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-in">
        {stats.map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-md"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.07]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${s.color} text-white shadow-lg`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {isLoading ? '—' : s.value}
                </p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {!awaitingTenantChoice && (
        <ListFilterBar
          chips={buildStatusChips(t)}
          chipValue={search.status ?? 'all'}
          onChipChange={(value) =>
            setFilters({ status: value === 'all' ? undefined : value })
          }
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder={t('filters.searchPlaceholder.merchants')}
          isSearchPending={isSearchPending}
          activeCount={activeCount}
          onReset={reset}
          isPending={isFetching}
        />
      )}

      {/* Table */}
      <Card className="shadow-md border-0">
        <CardContent className="p-0">
          {awaitingTenantChoice ? (
            <div className="p-12 text-center text-muted-foreground">
              <Building className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium text-foreground">Select a tenant</p>
              <p className="text-sm">
                Merchants belong to a tenant. Pick one from the selector at the
                top of the sidebar to see its merchants.
              </p>
            </div>
          ) : isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6">
              <ApiErrorCard
                error={error}
                onRetry={() => refetch()}
                title="Failed to load merchants"
              />
            </div>
          ) : allMerchants.length === 0 ? (
            <NoFilterResults
              isFiltered={activeCount > 0}
              emptyTitle="No merchants yet"
              emptyDescription="Onboard a merchant to start issuing merchant credit."
              icon={ShoppingBag}
              onReset={reset}
              action={
                <Button onClick={() => navigate({ to: '/merchants/new' })}>
                  <Plus className="w-4 h-4 mr-2" /> Add Merchant
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Merchant</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Collection</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Onboarded</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allMerchants.map((m) => (
                    <TableRow
                      key={m.id}
                      className="hover:bg-muted/30 transition-colors cursor-pointer row-hover-lift"
                      onClick={() =>
                        navigate({
                          to: '/merchants/$merchantId',
                          params: { merchantId: m.id },
                        })
                      }
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {avatar(m.businessName)}
                          <span className="font-medium">{m.businessName}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-sm">
                        {m.ownerPhone}
                      </TableCell>
                      <TableCell>{modeBadge(m.collectionMode)}</TableCell>
                      <TableCell>
                        <MerchantStatusBadge status={m.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(m.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
